// @/services/openai/chatCompletionService.js
import { API_BASE_URL } from "@/lib/constants/app";
import { userTokenStats } from "@/lib/db/utils/supabase";
import useAuthStore from "@/store/authStore";

// Chat Completion Service - Now uses backend API
export class ChatCompletionService {
  constructor() {
    // Main settings
    this.model = "gpt-4.1-mini";
    this.systemPrompt = null;

    // Default settings
    this.temperature = 0.5;
    this.maxTokens = 4096;
    this.topP = 1;
    this.frequencyPenalty = 0;
    this.presencePenalty = 0;

    // State for chat completion
    this.messages = [];
    this.response = null;
    this.isLoading = false;
    this.error = null;

    // Image generation defaults - Updated for Qwen image generation
    this.imageDefaults = {
      model: "qwen-image",
      size: "1024x1024",
      quality: "medium",
      n: 1,
    };

    // State for unique completion results
    this.results = {
      title: null,
      summary: null,
      conversation: null,
      image: null,
    };

    // Try to get user ID from auth store
    try {
      const authStore = useAuthStore.getState();
      this.userId = authStore?.user?.id;
      this.workspaceId = authStore?.activeWorkspace?.id;
    } catch (error) {
      console.warn("Could not get user ID from auth store:", error);
      this.userId = null;
      this.workspaceId = null;
    }
  }

  // Helper method to get image generation endpoint based on provider and model
  getImageEndpoint(provider, model) {
    const routes = {
      qwen: `/image/generate/qwen/${model}`,
      openai: `/image/generate/openai/${model}`,
      flux: `/image/generate/flux/${model}`,
      gemini: `/image/generate/gemini/${model}`,
      imagen: `/image/generate/imagen/${model}`,
      xai: `/image/generate/xai/${model}`,
    };
    return routes[provider] || `/image/generate/qwen/${model || "qwen-image"}`;
  }

  // Helper method to get image editing endpoint based on provider and model
  getImageEditEndpoint(provider, model) {
    const routes = {
      qwen: `/image/edit/qwen/${model}`,
      openai: `/image/edit/openai/${model}`,
      gemini: `/image/edit/gemini/${model}`,
      flux: `/image/edit/flux/${model}`,
      bytedance: `/image/edit/bytedance/${model}`,
    };
    return routes[provider] || `/image/edit/openai/${model || "gpt-image-1"}`;
  }

  // Utility method to make API requests to backend
  async makeBackendRequest(
    endpoint,
    data = {},
    method = "POST",
    signal = null
  ) {
    try {
      const fetchOptions = {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: method === "GET" ? undefined : JSON.stringify(data),
      };

      // Add signal if provided
      if (signal) {
        fetchOptions.signal = signal;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/ai${endpoint}`,
        fetchOptions
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Backend API error for ${endpoint}:`, error);
      throw error;
    }
  }

  // Utility method to track token usage (kept for compatibility)
  async trackTokenUsage(usageData, modelId, featureId = "chat") {
    if (!this.userId || !usageData) return;

    try {
      await userTokenStats.recordOpenAIUsage({
        userId: this.userId,
        workspaceId: this.workspaceId,
        usageData,
        modelId,
        featureId,
        metadata: {
          messageCount: this.messages?.length || 0,
        },
      });
    } catch (error) {
      console.error("Failed to track token usage:", error);
    }
  }

  // Setters (kept for compatibility)
  setSystemPrompt(prompt) {
    this.systemPrompt = prompt;
  }
  setModel(model) {
    this.model = model;
  }
  setTemperature(temperature) {
    this.temperature = temperature;
  }
  setMaxTokens(maxTokens) {
    this.maxTokens = maxTokens;
  }
  setTopP(topP) {
    this.topP = topP;
  }
  setFrequencyPenalty(frequencyPenalty) {
    this.frequencyPenalty = frequencyPenalty;
  }
  setPresencePenalty(presencePenalty) {
    this.presencePenalty = presencePenalty;
  }
  setMessages(messages) {
    this.messages = messages;
  }
  setResponse(response) {
    this.response = response;
  }
  setIsLoading(isLoading) {
    this.isLoading = isLoading;
  }
  setError(error) {
    this.error = error;
  }

  // Create chat completion using backend API
  async createChatCompletion(messages = this.messages, options = {}) {
    this.isLoading = true;
    this.error = null;

    try {
      // Prepare messages array with system prompt if exists
      const messageArray = [];
      if (this.systemPrompt) {
        messageArray.push({
          role: "system",
          content: this.systemPrompt,
        });
      }
      messageArray.push(...messages);

      const requestData = {
        provider: "openai",
        model: options.model || this.model,
        messages: messageArray,
        temperature: options.temperature || this.temperature,
        max_tokens: options.maxTokens || this.maxTokens,
        top_p: options.topP || this.topP,
        frequency_penalty: this.frequencyPenalty,
        presence_penalty: this.presencePenalty,
        response_format: options.response_format,
      };

      const response = await this.makeBackendRequest(
        "/chat/completion",
        requestData
      );

      // Track token usage if available
      if (response.result?.usage) {
        await this.trackTokenUsage(
          response.result.usage,
          options.model || this.model,
          options.featureId || "chat_completion"
        );
      }

      this.response = response.result;
      this.isLoading = false;
      return response.result;
    } catch (error) {
      this.error = error;
      this.isLoading = false;
      throw error;
    }
  }

  // Get chat response
  async getChatResponse(messages = this.messages, options = {}) {
    try {
      const response = await this.createChatCompletion(messages, options);
      return response.choices[0].message.content;
    } catch (error) {
      console.error("Error getting chat response:", error);
      throw error;
    }
  }

  // Generate a title using backend API
  async generateTitle(options = {}) {
    if (!this.messages || this.messages.length < 2) {
      return "New Chat";
    }

    try {
      const response = await this.makeBackendRequest("/chat/title", {
        messages: this.messages,
      });

      // Track token usage if available
      if (response.usage) {
        await this.trackTokenUsage(
          response.usage,
          "gpt-4.1-nano",
          "title_generation"
        );
      }

      const title = response.title;
      this.results.title = title;
      return title;
    } catch (error) {
      console.error("Error generating title:", error);
      return "New Chat";
    }
  }

  // Generate a summary using backend API
  async generateSummary(options = {}) {
    if (!this.messages || this.messages.length < 2) {
      return "No summary available";
    }

    try {
      const response = await this.makeBackendRequest("/chat/summary", {
        messages: this.messages,
      });

      // Track token usage if available
      if (response.usage) {
        await this.trackTokenUsage(
          response.usage,
          "gpt-4.1-nano",
          "summary_generation"
        );
      }

      const summary = response.summary;
      this.results.summary = summary;
      return summary;
    } catch (error) {
      console.error("Error generating summary:", error);
      return "Error generating summary";
    }
  }

  /**
   * Generate image using Qwen image generation engine
   * @param {Object} params - Image generation parameters
   * @returns {Promise<Object>} Generated image response
   */
  async generateImage(params = {}) {
    try {
      const {
        prompt,
        model = "qwen-image",
        size = "1024x1024",
        quality = "medium",
        n = 1,
      } = params;

      if (!prompt) {
        throw new Error("Prompt is required for image generation");
      }

      // Build endpoint based on provider and model
      const { provider, model: modelId, ...otherParams } = params;
      const endpoint = this.getImageEndpoint(provider, modelId);

      console.log("🚀 [Image Generation API] Making request to backend:", {
        endpoint,
        prompt: prompt.substring(0, 100) + (prompt.length > 100 ? "..." : ""),
        promptLength: prompt.length,
        provider,
        model: modelId,
        params: otherParams,
        timestamp: new Date().toISOString(),
      });

      const response = await this.makeBackendRequest(endpoint, {
        prompt,
        model: modelId,
        ...otherParams,
      });

      console.log("✅ [Image Generation API] Backend response received:", {
        hasResult: !!response.result,
        hasData: !!response.result?.data,
        imageCount: response.result?.data?.length || 0,
        responseKeys: Object.keys(response || {}),
        resultKeys: response.result ? Object.keys(response.result) : [],
        hasError: !!response.error,
        error: response.error,
        success: response.success,
        model: modelId,
        provider,
        timestamp: new Date().toISOString(),
      });

      // Check for error response first (backend returns { error: "...", details: "..." } on error)
      if (response.error) {
        const errorMessage =
          response.details ||
          response.error ||
          response.message ||
          "Unknown error from backend";
        console.error("❌ [Image Generation API] Backend returned error:", {
          error: errorMessage,
          errorField: response.error,
          detailsField: response.details,
          errorType: response.errorType,
          errorStatus: response.errorStatus,
          fullResponse: response,
          timestamp: new Date().toISOString(),
        });
        const error = new Error(`Image generation failed: ${errorMessage}`);
        error.errorType = response.errorType || "general";
        error.errorStatus = response.errorStatus;
        throw error;
      }

      // Check if success is explicitly false
      if (response.success === false) {
        const errorMessage =
          response.error ||
          response.message ||
          response.details ||
          "Image generation failed";
        console.error(
          "❌ [Image Generation API] Backend returned success: false:",
          {
            error: errorMessage,
            errorType: response.errorType,
            errorStatus: response.errorStatus,
            fullResponse: response,
            timestamp: new Date().toISOString(),
          }
        );
        const error = new Error(`Image generation failed: ${errorMessage}`);
        error.errorType = response.errorType || "general";
        error.errorStatus = response.errorStatus;
        throw error;
      }

      // Check if result exists (should be present for successful responses)
      if (!response.result) {
        console.error(
          "❌ [Image Generation API] Backend response missing result:",
          {
            response,
            responseKeys: Object.keys(response || {}),
            timestamp: new Date().toISOString(),
          }
        );
        throw new Error(
          "Backend response missing result data. The image generation may have failed."
        );
      }

      return response.result;
    } catch (error) {
      console.error("❌ [Image Generation API] Error generating image:", {
        error: error.message,
        prompt:
          params.prompt?.substring(0, 100) +
          (params.prompt?.length > 100 ? "..." : ""),
        model: params.model || "qwen-image",
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  /**
   * Edit image using GPT-Image-1
   * @param {Object} params - Image editing parameters
   * @returns {Promise<Object>} Edited image response
   */
  async editImage(params = {}) {
    try {
      const {
        prompt,
        image,
        mask,
        model = "dall-e-2",
        provider = "openai",
        signal,
        ...otherParams
      } = params;

      if (!prompt) {
        throw new Error("Prompt is required for image editing");
      }

      if (!image) {
        throw new Error("Image is required for editing");
      }

      const endpoint = this.getImageEditEndpoint(provider, model);

      // Transform parameters based on provider
      let requestBody;
      if (provider === "gemini" || provider === "bytedance") {
        // Gemini and ByteDance expect referenceImages/image_urls array
        requestBody = {
          prompt,
          referenceImages: [image],
          model,
          ...otherParams,
        };
      } else if (provider === "flux") {
        // FLUX expects image_url (singular)
        requestBody = {
          prompt,
          image_url: image,
          model,
          ...otherParams,
        };
      } else if (provider === "qwen") {
        // Qwen expects imageUrl
        requestBody = {
          prompt,
          imageUrl: image,
          model,
          ...otherParams,
        };
      } else {
        // OpenAI and others expect image
        requestBody = {
          prompt,
          image,
          mask,
          model,
          ...otherParams,
        };
      }

      const response = await this.makeBackendRequest(
        endpoint,
        requestBody,
        "POST",
        signal
      );

      return response.result;
    } catch (error) {
      console.error("Error editing image:", error);
      throw error;
    }
  }

  /**
   * Create vector store
   * @param {string} name - Vector store name
   * @returns {Promise<Object>} Vector store response
   */
  async createVectorStore(name) {
    try {
      if (!name) {
        throw new Error("Vector store name is required");
      }

      const response = await this.makeBackendRequest("/vector-store/create", {
        name,
      });

      return response.vectorStore;
    } catch (error) {
      console.error("Error creating vector store:", error);
      throw error;
    }
  }

  /**
   * Upload file and attach to vector store in one operation (recommended)
   * @param {string} vectorStoreId - Vector store ID
   * @param {string} content - File content
   * @param {string} filename - File name
   * @returns {Promise<Object>} Upload and attach response
   */
  async uploadAndAttachToVectorStore(vectorStoreId, content, filename) {
    console.log("uploadAndAttachToVectorStore called with:", {
      vectorStoreId,
      filename,
      contentLength: content?.length,
      contentType: typeof content,
    });

    try {
      if (!vectorStoreId || !content || !filename) {
        throw new Error("Vector store ID, content, and filename are required");
      }

      const response = await this.makeBackendRequest(
        `/vector-store/${vectorStoreId}/files/upload-and-attach`,
        {
          content,
          filename,
        }
      );

      console.log("uploadAndAttachToVectorStore response:", {
        success: response.success,
        fileUploadId: response.fileUpload?.id,
        uploadResponseId: response.uploadResponse?.id,
      });

      return response;
    } catch (error) {
      console.error(
        "Error uploading and attaching file to vector store:",
        error
      );
      throw error;
    }
  }

  /**
   * Upload file to vector store (legacy method - now uses combined approach)
   * @param {string} vectorStoreId - Vector store ID
   * @param {string} content - File content
   * @param {string} filename - File name
   * @returns {Promise<Object>} Upload response
   */
  async uploadFileToVectorStore(vectorStoreId, content, filename) {
    console.log("uploadFileToVectorStore called with:", {
      vectorStoreId,
      filename,
      contentLength: content?.length,
      contentType: typeof content,
    });

    // Use the new combined upload-and-attach method for better reliability
    return this.uploadAndAttachToVectorStore(vectorStoreId, content, filename);
  }

  /**
   * Get vector store details
   * @param {string} vectorStoreId - Vector store ID
   * @returns {Promise<Object>} Vector store details
   */
  async getVectorStore(vectorStoreId) {
    try {
      if (!vectorStoreId) {
        throw new Error("Vector store ID is required");
      }

      const response = await this.makeBackendRequest(
        `/vector-store/${vectorStoreId}`,
        {},
        "GET"
      );

      return response.vectorStore;
    } catch (error) {
      console.error("Error getting vector store:", error);
      throw error;
    }
  }

  /**
   * List vector stores
   * @param {Object} params - List parameters
   * @returns {Promise<Array>} Array of vector stores
   */
  async listVectorStores(params = {}) {
    try {
      const { limit = 20, order = "desc", after, before } = params;

      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        order,
      });

      if (after) queryParams.append("after", after);
      if (before) queryParams.append("before", before);

      const response = await this.makeBackendRequest(
        `/vector-store/list?${queryParams.toString()}`,
        {},
        "GET"
      );

      return response.vectorStores;
    } catch (error) {
      console.error("Error listing vector stores:", error);
      throw error;
    }
  }

  /**
   * Delete vector store
   * @param {string} vectorStoreId - Vector store ID
   * @returns {Promise<Object>} Delete response
   */
  async deleteVectorStore(vectorStoreId) {
    try {
      if (!vectorStoreId) {
        throw new Error("Vector store ID is required");
      }

      const response = await this.makeBackendRequest(
        `/vector-store/${vectorStoreId}`,
        {},
        "DELETE"
      );

      return response.result;
    } catch (error) {
      console.error("Error deleting vector store:", error);
      throw error;
    }
  }

  /**
   * Delete file from vector store
   * @param {string} vectorStoreId - Vector store ID
   * @param {string} fileId - File ID to delete
   * @returns {Promise<Object>} Delete response
   */
  async deleteFileFromVectorStore(vectorStoreId, fileId) {
    try {
      if (!vectorStoreId || !fileId) {
        throw new Error("Vector store ID and file ID are required");
      }

      const response = await this.makeBackendRequest(
        `/vector-store/${vectorStoreId}/files/${fileId}`,
        {},
        "DELETE"
      );

      return response.result;
    } catch (error) {
      console.error("Error deleting file from vector store:", error);
      throw error;
    }
  }

  /**
   * Search vector store
   * @param {string} vectorStoreId - Vector store ID
   * @param {string} query - Search query
   * @param {number} maxNumResults - Maximum number of results to return (default: 10, max: 50)
   * @param {Object} options - Additional search options
   * @returns {Promise<Object>} Search results
   */
  async searchVectorStore(
    vectorStoreId,
    query,
    maxNumResults = 10,
    options = {}
  ) {
    try {
      if (!vectorStoreId) {
        throw new Error("Vector store ID is required");
      }

      if (!query || typeof query !== "string") {
        throw new Error("Query is required and must be a string");
      }

      // Ensure maxNumResults is within valid range
      const validMaxResults = Math.min(Math.max(maxNumResults, 1), 50);

      const requestBody = {
        query,
        max_num_results: validMaxResults,
      };

      // Add optional parameters
      if (options.rewrite_query) {
        requestBody.rewrite_query = options.rewrite_query;
      }
      if (options.attribute_filter) {
        requestBody.attribute_filter = options.attribute_filter;
      }
      if (options.ranking_options) {
        requestBody.ranking_options = options.ranking_options;
      }

      const response = await this.makeBackendRequest(
        `/vector-store/${vectorStoreId}/search`,
        requestBody
      );

      return response.results;
    } catch (error) {
      console.error("Error searching vector store:", error);
      throw error;
    }
  }

  // Create image completion using backend API
  async createImageCompletion(prompt, options = {}) {
    this.isLoading = true;
    this.error = null;

    try {
      const imageOptions = {
        ...this.imageDefaults,
        ...options,
        prompt,
        // Qwen image generation always returns base64-encoded images, no response_format needed
      };

      const response = await this.makeBackendRequest(
        "/image/generate/qwen/qwen-image",
        imageOptions
      );

      this.results.image = {
        imageData: response.result.data[0].b64_json,
        prompt,
        metadata: {
          model: imageOptions.model,
          size: imageOptions.size,
          quality: imageOptions.quality,
          generatedAt: new Date().toISOString(),
        },
      };

      this.isLoading = false;
      return this.results.image;
    } catch (error) {
      this.error = error;
      this.isLoading = false;
      throw error;
    }
  }

  // Create streaming chat completion with JSON parsing callbacks
  // This streams JSON responses and calls back as fields are extracted
  async createStreamingJsonCompletion(messages, options = {}) {
    this.isLoading = true;
    this.error = null;

    const {
      systemPrompt: optionSystemPrompt,
      onFieldUpdate,
      onComplete,
      onError,
      signal,
      ...otherOptions
    } = options;

    try {
      const systemPrompt = optionSystemPrompt || this.systemPrompt;

      // Ensure we have user ID - fetch fresh from auth store if not set
      let userId = this.userId;
      let workspaceId = this.workspaceId;
      if (!userId) {
        try {
          const authStore = useAuthStore.getState();
          userId = authStore?.user?.id;
          workspaceId = authStore?.activeWorkspace?.id;
        } catch {
          // Ignore - will use null
        }
      }

      // Prepare messages array with system prompt if exists
      const messageArray = [];
      if (systemPrompt) {
        messageArray.push({
          role: "system",
          content: systemPrompt,
        });
      }
      messageArray.push(...messages);

      const requestBody = {
        provider: "openai",
        model: otherOptions.model || this.model,
        messages: messageArray,
        temperature: otherOptions.temperature || this.temperature,
        max_tokens: otherOptions.maxTokens || this.maxTokens,
        // Note: response_format with json_object is not supported in streaming mode
        // The system prompt should instruct the model to return JSON
        // Include tracking info to satisfy backend middleware requirements
        tracking: {
          userId: userId,
          workspaceId: workspaceId,
          threadId: otherOptions.threadId || `assistant-gen-${Date.now()}`,
          featureId: otherOptions.featureId || "assistant_generation",
        },
      };

      const response = await fetch(`${API_BASE_URL}/api/ai/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Backend API Error (${response.status}): ${errorData.error || response.statusText}`
        );
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let fullContent = "";
      let lastParsedData = {};

      // Helper to extract complete field values from partial JSON
      const extractFieldsFromPartialJson = (jsonStr) => {
        const fields = {};

        // Try to parse complete JSON first
        try {
          const parsed = JSON.parse(jsonStr);
          return { parsed, isComplete: true };
        } catch {
          // Not complete JSON, extract what we can
        }

        // Extract "name" field - look for complete string value
        const nameMatch = jsonStr.match(/"name"\s*:\s*"([^"]+)"/);
        if (nameMatch) {
          fields.name = nameMatch[1];
        }

        // Extract "description" field
        const descMatch = jsonStr.match(/"description"\s*:\s*"([^"]*)/);
        if (descMatch) {
          // Find where description value ends (either at next field or incomplete)
          const afterDesc = jsonStr.substring(
            jsonStr.indexOf(descMatch[0]) + descMatch[0].length
          );
          const endQuoteMatch = afterDesc.match(/^([^"]*?)"/);
          if (endQuoteMatch) {
            fields.description = descMatch[1] + endQuoteMatch[1];
          } else {
            // Still streaming this field
            fields.description = descMatch[1] + afterDesc.replace(/\\n/g, "\n");
          }
        }

        // Extract "instructions" field (can be very long)
        const instrMatch = jsonStr.match(/"instructions"\s*:\s*"([^"]*)/);
        if (instrMatch) {
          const afterInstr = jsonStr.substring(
            jsonStr.indexOf(instrMatch[0]) + instrMatch[0].length
          );
          // Look for the closing quote that ends the instructions
          const endQuoteMatch = afterInstr.match(/^([^"]*?)"\s*[,}]/);
          if (endQuoteMatch) {
            fields.instructions = (instrMatch[1] + endQuoteMatch[1])
              .replace(/\\n/g, "\n")
              .replace(/\\"/g, '"');
          } else {
            // Still streaming this field
            fields.instructions = (instrMatch[1] + afterInstr)
              .replace(/\\n/g, "\n")
              .replace(/\\"/g, '"');
          }
        }

        // Extract other simple fields
        const simpleFields = [
          "primary_purpose",
          "recommended_model",
          "reasoning",
        ];
        for (const field of simpleFields) {
          const regex = new RegExp(`"${field}"\\s*:\\s*"([^"]+)"`);
          const match = jsonStr.match(regex);
          if (match) {
            fields[field] = match[1];
          }
        }

        // Extract "recommended_tools" array
        const toolsMatch = jsonStr.match(
          /"recommended_tools"\s*:\s*\[([^\]]*)\]/
        );
        if (toolsMatch) {
          const toolsStr = toolsMatch[1];
          const tools = toolsStr.match(/"([^"]+)"/g);
          if (tools) {
            fields.recommended_tools = tools.map((t) => t.replace(/"/g, ""));
          }
        }

        return { parsed: fields, isComplete: false };
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process SSE events
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data === "[DONE]") continue;

            try {
              const parsed = JSON.parse(data);

              // Handle different SSE event types
              // Backend sends: { type: "chunk", sessionId, chunk } where chunk is the OpenAI delta
              if (parsed.type === "chunk" && parsed.chunk) {
                // Extract content from the chunk - handle OpenAI's delta format
                const chunkContent =
                  parsed.chunk.choices?.[0]?.delta?.content ||
                  parsed.chunk.content ||
                  "";
                if (chunkContent) {
                  fullContent += chunkContent;

                  // Try to extract fields from the accumulated content
                  const { parsed: extractedFields, isComplete } =
                    extractFieldsFromPartialJson(fullContent);

                  if (isComplete) {
                    // Full JSON parsed successfully
                    if (onFieldUpdate) {
                      onFieldUpdate(extractedFields, true);
                    }
                    lastParsedData = extractedFields;
                  } else if (Object.keys(extractedFields).length > 0) {
                    // Partial extraction - call back with updated fields
                    const changedFields = {};
                    let hasChanges = false;

                    for (const [key, value] of Object.entries(extractedFields)) {
                      if (value !== lastParsedData[key]) {
                        changedFields[key] = value;
                        hasChanges = true;
                      }
                    }

                    if (hasChanges && onFieldUpdate) {
                      onFieldUpdate(
                        { ...lastParsedData, ...extractedFields },
                        false
                      );
                      lastParsedData = { ...lastParsedData, ...extractedFields };
                    }
                  }
                }
              } else if (parsed.type === "done" || parsed.type === "complete") {
                // Stream complete
                break;
              } else if (parsed.type === "error") {
                throw new Error(parsed.error || "Stream error");
              }
            } catch (parseError) {
              // Skip malformed SSE data
              console.warn("Failed to parse SSE data:", parseError);
            }
          }
        }
      }

      // Final parse attempt
      try {
        const finalData = JSON.parse(fullContent);
        if (onComplete) {
          onComplete(finalData);
        }
        this.isLoading = false;
        return finalData;
      } catch {
        // Return what we extracted
        if (onComplete) {
          onComplete(lastParsedData);
        }
        this.isLoading = false;
        return lastParsedData;
      }
    } catch (error) {
      this.error = error;
      this.isLoading = false;
      if (onError) {
        onError(error);
      }
      throw error;
    }
  }

  // Create chat completion with structured output using backend API
  async createChatCompletionWithStructuredOutput(messages, options = {}) {
    this.isLoading = true;
    this.error = null;

    try {
      // Use systemPrompt from options if provided, otherwise use instance systemPrompt
      const systemPrompt = options.systemPrompt || this.systemPrompt;

      // Prepare messages array with system prompt if exists
      const messageArray = [];
      if (systemPrompt) {
        messageArray.push({
          role: "system",
          content: systemPrompt,
        });
      }
      messageArray.push(...messages);

      const requestData = {
        provider: "openai",
        model: options.model || this.model,
        messages: messageArray,
        temperature: options.temperature || this.temperature,
        max_tokens: options.maxTokens || this.maxTokens,
        top_p: options.topP || this.topP,
        frequency_penalty: this.frequencyPenalty,
        presence_penalty: this.presencePenalty,
        response_format: options.response_format || { type: "json_object" },
      };

      const response = await this.makeBackendRequest(
        "/chat/completion",
        requestData
      );

      // Track token usage if available
      if (response.result?.usage) {
        await this.trackTokenUsage(
          response.result.usage,
          options.model || this.model,
          options.featureId || "structured_completion"
        );
      }

      this.response = response.result;
      this.isLoading = false;
      return response.result;
    } catch (error) {
      this.error = error;
      this.isLoading = false;
      throw error;
    }
  }

  // Helper method to generate assistant avatar prompt using backend API
  async generateAvatarPrompt(systemMessage, assistantMessages) {
    try {
      const response = await this.makeBackendRequest(
        "/assistant/avatar-prompt",
        {
          systemMessage,
          assistantMessages,
        }
      );

      // Track token usage if available
      if (response.usage) {
        await this.trackTokenUsage(
          response.usage,
          "gpt-4o-mini",
          "assistant_generation"
        );
      }

      return response.avatarPrompt;
    } catch (error) {
      console.error("Error generating avatar prompt:", error);
      throw error;
    }
  }

  // Utility method to validate API key using backend
  async validateApiKey(apiKey) {
    try {
      const response = await this.makeBackendRequest("/validate-key", {
        apiKey,
      });

      return response.valid;
    } catch (error) {
      console.error("API key validation failed:", error);
      return false;
    }
  }

  // Generate memory filename using backend API
  async generateMemoryFilename(content) {
    try {
      const response = await this.makeBackendRequest("/memory/filename", {
        content,
      });

      // Track token usage if available
      if (response.usage) {
        await this.trackTokenUsage(
          response.usage,
          "gpt-4o-mini",
          "memory_filename_generation"
        );
      }

      return {
        filename: response.filename,
        title: response.title,
      };
    } catch (error) {
      console.error("Error generating memory filename:", error);
      // Fallback to a generic filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      return {
        filename: `memory-${timestamp}.txt`,
        title: "Saved Memory",
      };
    }
  }

  // Suggest tools from instructions using backend API
  async suggestToolsFromInstructions(instructions) {
    try {
      const response = await this.makeBackendRequest(
        "/assistant/suggest-tools",
        {
          instructions,
        }
      );

      // Track token usage if available
      if (response.usage) {
        await this.trackTokenUsage(
          response.usage,
          "gpt-4o-mini",
          "tool_suggestion"
        );
      }

      return {
        recommended_tools: response.recommendedTools,
        reasoning: response.reasoning,
      };
    } catch (error) {
      console.error("Error suggesting tools:", error);
      throw error;
    }
  }

  /**
   * Upload file to OpenAI
   * @param {File|Blob|string} file - File to upload (File object, Blob, data URL, or base64)
   * @param {string} purpose - Purpose of the file (default: "assistants")
   * @param {string} filename - Optional filename override
   * @returns {Promise<Object>} Upload response
   */
  async uploadFile(file, purpose = "assistants", filename = null) {
    try {
      if (!file) {
        throw new Error("File is required for upload");
      }

      // Prepare file data for backend
      let fileData;
      let actualFilename = filename;

      if (file instanceof File) {
        // Convert File to base64 for JSON transmission
        const buffer = await file.arrayBuffer();
        const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
        fileData = `data:${file.type};base64,${base64}`;
        actualFilename = actualFilename || file.name;
      } else if (file instanceof Blob) {
        // Convert Blob to base64
        const buffer = await file.arrayBuffer();
        const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
        fileData = `data:application/octet-stream;base64,${base64}`;
        actualFilename = actualFilename || "uploaded_file";
      } else if (typeof file === "string") {
        // Assume it's already a data URL or base64
        fileData = file;
        actualFilename = actualFilename || "uploaded_file";
      } else {
        throw new Error("Invalid file format");
      }

      const response = await this.makeBackendRequest("/files/upload", {
        file: fileData,
        purpose,
        filename: actualFilename,
      });

      // Record token usage if available
      if (response.usage) {
        await this.trackTokenUsage(
          response.usage,
          "file-upload",
          "file_operations"
        );
      }

      return response.file;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  }

  /**
   * Retrieve file details from OpenAI
   * @param {string} fileId - File ID to retrieve
   * @returns {Promise<Object>} File details
   */
  async retrieveFile(fileId) {
    try {
      if (!fileId) {
        throw new Error("File ID is required");
      }

      const response = await this.makeBackendRequest(
        `/files/${fileId}`,
        {},
        "GET"
      );

      return response.file;
    } catch (error) {
      console.error("Error retrieving file:", error);
      throw error;
    }
  }

  /**
   * List files from OpenAI
   * @param {Object} params - List parameters
   * @returns {Promise<Object>} Files list response
   */
  async listFiles(params = {}) {
    try {
      const { purpose, limit = 20, order = "desc", after } = params;

      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        order,
      });

      if (purpose) queryParams.append("purpose", purpose);
      if (after) queryParams.append("after", after);

      const response = await this.makeBackendRequest(
        `/files?${queryParams.toString()}`,
        {},
        "GET"
      );

      return {
        files: response.files,
        hasMore: response.hasMore,
      };
    } catch (error) {
      console.error("Error listing files:", error);
      throw error;
    }
  }

  /**
   * Delete file from OpenAI
   * @param {string} fileId - File ID to delete
   * @returns {Promise<Object>} Delete response
   */
  async deleteFile(fileId) {
    try {
      if (!fileId) {
        throw new Error("File ID is required");
      }

      const response = await this.makeBackendRequest(
        `/files/${fileId}`,
        {},
        "DELETE"
      );

      return response.result;
    } catch (error) {
      console.error("Error deleting file:", error);
      throw error;
    }
  }

  /**
   * Retrieve file content from OpenAI
   * @param {string} fileId - File ID to download
   * @returns {Promise<Blob>} File content as blob
   */
  async retrieveFileContent(fileId) {
    try {
      if (!fileId) {
        throw new Error("File ID is required");
      }

      // Use fetch directly for binary content
      const response = await fetch(
        `${API_BASE_URL}/api/ai/files/${fileId}/content`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error("Error retrieving file content:", error);
      throw error;
    }
  }

  /**
   * Generate personalized assistant types based on user interests and context
   * @param {Array} interests - User's selected interests
   * @param {string} profileContext - User's custom instructions/context
   * @param {string} displayName - User's display name
   * @param {Array} traits - User's personality traits
   * @returns {Promise<Array>} Array of 6 personalized assistant types (5 specialized + 1 personal)
   */
  async generatePersonalizedAssistantTypes(
    interests,
    profileContext = "",
    displayName = "User",
    traits = []
  ) {
    // Define variables at method scope so they're available in both try and catch blocks
    const interestsText =
      interests.length > 0 ? interests.join(", ") : "general productivity";
    const traitsText =
      traits.length > 0 ? traits.join(", ") : "professional and helpful";

    try {
      // First, create the personal assistant
      const personalAssistant = {
        name: `${displayName}'s Personal AI`,
        description: `A personalized AI companion designed specifically for ${displayName}, optimized for general tasks, daily assistance, and tailored to their unique interests and communication style.`,
        base_instructions: `You are ${displayName}'s personal AI companion, designed to be their go-to assistant for daily tasks, general questions, and personalized support.

**Core Responsibilities:**
- Provide personalized assistance tailored to ${displayName}'s interests in ${interestsText}
- Help with daily tasks, planning, and organization
- Offer general knowledge and research support
- Assist with creative tasks and problem-solving
- Maintain a ${traitsText || "professional and helpful"} communication style

**Methodology:**
- Always address ${displayName} by name when appropriate
- Adapt responses to their interests and professional context
- Provide practical, actionable advice
- Use web search for current information when needed
- Generate images to enhance explanations or for creative projects

**Output Requirements:**
- Personalized responses that reflect ${displayName}'s preferences
- Clear, actionable guidance for tasks and questions
- Engaging and helpful communication style
- Comprehensive answers that anticipate follow-up needs

**Quality Standards:**
- Maintain consistency with ${displayName}'s preferred communication style
- Provide accurate, up-to-date information
- Offer creative solutions and alternatives
- Be proactive in suggesting helpful resources or next steps`,
        primary_purpose: "general",
        recommended_model: "gpt-4o-mini",
        tools: ["web_search", "image_gen"],
        reasoning: `A personalized general assistant that knows ${displayName}'s interests and can help with day-to-day tasks while maintaining their preferred communication style.`,
      };

      const systemPrompt = `You are an AI Assistant Architect specializing in creating personalized AI assistants with optimal model selection. Your role is to analyze a user's interests, professional context, and personality traits to generate 5 highly customized specialist assistant types that complement their personal AI.

**Core Principles:**
- Generate assistants that are HIGHLY SPECIFIC to the user's context and interests
- Each assistant should have a unique focus that complements the others
- Names should be professional titles (Specialist, Expert, Advisor, Analyst, etc.) - NEVER use "Assistant"
- Consider the user's specific domain expertise and tailor assistants accordingly
- Think about workflow gaps that custom assistants could fill
- These assistants will work alongside a personal general-purpose AI
- Assign the optimal AI model for each assistant's specific tasks

**OPTIMAL MODEL ASSIGNMENT GUIDE:**

**gpt-4o** - Use for:
- Creative writing, content creation, storytelling
- Customer support, communication, correspondence
- Marketing copy, social media content
- General conversation, personal assistance
- Educational content, tutoring, explanations
- Brand voice development, tone guidance

**gpt-4o-mini** - Use for:
- Quick tasks, simple queries, general assistance
- Cost-effective operations, high-volume tasks
- Basic research, information gathering
- Simple content creation, editing
- Administrative tasks, scheduling, organization
- Lightweight analysis, summaries

**gemini-2.5-pro** - Use for:
- Document analysis, large text processing
- Research synthesis, academic analysis
- Data interpretation, report generation
- Web application development, full-stack projects
- Multimodal tasks (when text + images needed)
- Strategic planning, business analysis

**deepseek-reasoner** - Use for:
- Mathematical reasoning, calculations, STEM
- Algorithmic problem solving
- Scientific research, modeling
- Cost-effective complex analysis
- Competitive programming tasks

**grok-3** - Use for:
- Real-time research, current events
- Market analysis, trend research
- Unconventional problem solving
- Social media analysis, brand monitoring
- Breaking news, live updates

**Analysis Framework:**
1. Analyze the user's interests and professional context
2. Identify 5 distinct SPECIALIZED areas where focused AI assistants would provide maximum value
3. Ensure assistants cover different aspects of their work/interests (strategy, execution, research, optimization, innovation)
4. Consider both immediate needs and advanced capabilities they might not have thought of
5. Assign the most suitable AI model for each assistant's primary tasks

**Assistant Quality Standards:**
- Each assistant should solve a specific, valuable problem
- Names should be compelling and professional
- Descriptions should clearly articulate the unique value proposition
- Base instructions should establish clear expertise and methodology
- Instructions should be in markdown format WITHOUT code block backticks
- Model selection should match the assistant's primary function and complexity needs`;

      const userPrompt = `Analyze this user profile and generate 5 highly specialized assistant types that complement their personal AI:

**User Profile:**
- Display Name: ${displayName}
- Interests: ${interestsText}
- Professional Context: "${profileContext}"
- Personality Traits: ${traitsText}

**Task:** Generate 5 custom SPECIALIST AI assistants that would be incredibly valuable for this specific user. These will work alongside their personal general-purpose AI, so focus on specialized expertise areas.

**Requirements:**
- Each assistant must be highly specific to their context and specialized in one area
- Names should be professional (use Specialist, Expert, Advisor, Analyst, Manager, etc.)
- NEVER use the word "Assistant" in names
- Consider both obvious needs and innovative applications
- Ensure variety across strategic, operational, creative, and analytical domains
- Instructions should be in markdown format WITHOUT backticks or code blocks

**Examples of good customization with optimal models:**
- For a full-stack AI developer:
  - "AI Ethics & Compliance Specialist" (deepseek-reasoner for complex analysis) - for complex analysis
  - "Model Performance Analyst" (gemini-2.5-pro for data analysis)
  - "AI Documentation Expert" (gpt-4o for technical writing)
- For a content creator:
  - "Audience Engagement Strategist" (gpt-4o for creative marketing)
  - "Content Performance Analyst" (gemini-2.5-pro for data interpretation)
  - "Brand Voice Consultant" (gpt-4o for brand voice development)
- For a finance professional:
  - "Risk Assessment Specialist" (deepseek-reasoner for complex analysis)
  - "Market Intelligence Analyst" (gemini-2.5-pro for research synthesis)
  - "Investment Research Expert" (gemini-2.5-pro for document analysis)

Generate exactly 5 specialist assistants with maximum relevance to this user's specific needs and optimal model assignments.

**Response Format:**
Return your response as a JSON object with this structure:

{
  "assistants": [
    {
      "name": "Professional Title (no 'Assistant')",
      "description": "Clear description of what this assistant does and its unique value",
      "base_instructions": "Comprehensive base instructions in markdown format WITHOUT backticks",
      "primary_purpose": "coding|writing|research|analysis|creative|business|specialized",
      "recommended_model": "gpt-4o|gpt-4o-mini|gemini-2.5-pro|deepseek-reasoner|grok-3",
      "tools": ["web_search", "image_gen"],
      "reasoning": "Brief explanation of why this assistant and model choice would be valuable for the user"
    }
  ]
}

**Model Selection Guidelines:**
- Choose "gpt-4o" for: creative writing, marketing, customer communication, brand work
- Choose "gpt-4o-mini" for: simple tasks, cost-effective operations, administrative work
- Choose "gemini-2.5-pro" for: document analysis, research synthesis, data interpretation, strategic planning
- Choose "deepseek-reasoner" for: mathematical reasoning, algorithmic tasks, STEM fields, cost-effective complex analysis
- Choose "grok-3" for: real-time research, current events, unconventional problem solving

Generate exactly 5 specialist assistants in this format.`;

      const messages = [{ role: "user", content: userPrompt }];

      const response = await this.createChatCompletionWithStructuredOutput(
        messages,
        {
          model: "gpt-4o",
          response_format: { type: "json_object" },
          featureId: "personalized_assistant_generation",
          systemPrompt: systemPrompt,
        }
      );

      const content = response.choices[0].message.content;
      const parsedData = JSON.parse(content.trim());

      // Validate and structure the response
      if (!parsedData.assistants || !Array.isArray(parsedData.assistants)) {
        throw new Error("Invalid response format: missing assistants array");
      }

      if (parsedData.assistants.length !== 5) {
        throw new Error(
          `Expected 5 specialist assistants, got ${parsedData.assistants.length}`
        );
      }

      // Validate each assistant has required fields and clean markdown formatting
      const validatedSpecialistAssistants = parsedData.assistants.map(
        (assistant, index) => {
          if (
            !assistant.name ||
            !assistant.description ||
            !assistant.base_instructions
          ) {
            throw new Error(`Assistant ${index + 1} missing required fields`);
          }

          // Ensure name doesn't contain "Assistant"
          if (assistant.name.toLowerCase().includes("assistant")) {
            console.warn(
              `Warning: Assistant name contains "Assistant": ${assistant.name}`
            );
          }

          // Clean markdown formatting - remove backticks and code block markers
          let cleanInstructions = assistant.base_instructions;
          if (typeof cleanInstructions === "string") {
            // Remove markdown code block backticks
            cleanInstructions = cleanInstructions.replace(/```[\w]*\n?/g, "");
            cleanInstructions = cleanInstructions.replace(/```/g, "");
            // Remove any remaining backticks around text
            cleanInstructions = cleanInstructions.replace(/`([^`]+)`/g, "$1");
            // Clean up any extra whitespace
            cleanInstructions = cleanInstructions.trim();
          }

          return {
            name: assistant.name,
            description: assistant.description,
            base_instructions: cleanInstructions,
            primary_purpose: assistant.primary_purpose || "specialized",
            recommended_model: assistant.recommended_model || "gpt-4o-mini",
            tools: assistant.tools || ["web_search", "image_gen"],
            reasoning:
              assistant.reasoning ||
              `Specialized assistant for ${assistant.name}`,
          };
        }
      );

      // Combine personal assistant with specialist assistants
      // Put personal assistant LAST so it can be generated after specialists and set as default
      const allAssistants = [
        ...validatedSpecialistAssistants,
        personalAssistant,
      ];

      console.log(
        `✅ Generated ${allAssistants.length} assistants (5 specialists + 1 personal) for ${displayName}`
      );

      return allAssistants;
    } catch (error) {
      console.error("Error generating personalized assistant types:", error);

      // Fallback to default assistants based on interests
      const { INTEREST_ASSISTANT_TEMPLATES } = await import(
        "@/lib/constants/interest-types"
      );

      // Create the personal assistant for fallback as well
      const fallbackPersonalAssistant = {
        name: `${displayName}'s Personal AI`,
        description: `A personalized AI companion designed specifically for ${displayName}, optimized for general tasks and daily assistance.`,
        base_instructions: `You are ${displayName}'s personal AI companion, designed to be their go-to assistant for daily tasks, general questions, and personalized support.

**Core Responsibilities:**
- Provide personalized assistance tailored to ${displayName}'s interests
- Help with daily tasks, planning, and organization
- Offer general knowledge and research support
- Assist with creative tasks and problem-solving
- Maintain a ${traitsText} communication style

**Methodology:**
- Always address ${displayName} by name when appropriate
- Provide practical, actionable advice
- Use web search for current information when needed
- Generate images to enhance explanations or for creative projects

**Output Requirements:**
- Personalized responses that reflect ${displayName}'s preferences
- Clear, actionable guidance for tasks and questions
- Engaging and helpful communication style

**Quality Standards:**
- Maintain consistency with ${displayName}'s preferred communication style
- Provide accurate, up-to-date information
- Offer creative solutions and alternatives`,
        primary_purpose: "general",
        recommended_model: "gpt-4o-mini",
        tools: ["web_search", "image_gen"],
        reasoning: `A personalized general assistant that can help ${displayName} with day-to-day tasks.`,
      };

      if (interests.length > 0) {
        const assistants = [];
        const usedNames = new Set();

        for (const interest of interests) {
          const templates = INTEREST_ASSISTANT_TEMPLATES[interest];
          if (templates && templates.types) {
            for (const template of templates.types) {
              if (assistants.length >= 5) break;
              if (!usedNames.has(template.name)) {
                assistants.push(template);
                usedNames.add(template.name);
              }
            }
          }
          if (assistants.length >= 5) break;
        }

        // Fill remaining slots with default if needed
        if (assistants.length < 5) {
          const defaultTemplates = INTEREST_ASSISTANT_TEMPLATES.default.types;
          for (const template of defaultTemplates) {
            if (assistants.length >= 5) break;
            if (!usedNames.has(template.name)) {
              assistants.push(template);
              usedNames.add(template.name);
            }
          }
        }

        // Add specialists first, then personal assistant last
        return [...assistants.slice(0, 5), fallbackPersonalAssistant];
      }

      // Ultimate fallback - 5 default assistants + personal assistant last
      const defaultAssistants =
        INTEREST_ASSISTANT_TEMPLATES.default.types.slice(0, 5);
      return [...defaultAssistants, fallbackPersonalAssistant];
    }
  }

  /**
   * Legacy compatibility method - creates files with the same interface as OpenAI client
   * @param {Object} params - File creation parameters
   * @returns {Promise<Object>} File creation response
   */
  async createFile(params) {
    const { file, purpose = "assistants" } = params;
    return this.uploadFile(file, purpose);
  }
}

export default ChatCompletionService;

// Create a service instance for immediate use
export const chatCompletionService = new ChatCompletionService();

// Legacy files object removed to prevent circular dependencies
// Use chatCompletionService methods directly instead
